import React, { useEffect, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import { useSiteContext } from "../SiteContext";
import { getPayrollBatches, processPayroll } from "../api/payroll";
import { PayrollBatchSummary, ProcessPayrollResponse } from "../interfaces/PayrollBatchInterface";
import { getDateString } from "../util/DateAndTimeUtil";
import Colors from "../styles/Colors.json";

const ProcessPayrollPage: React.FC = () => {
  const { setLoading, year, createPopup } = useSiteContext();

  const [batches, setBatches] = useState<PayrollBatchSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [processingID, setProcessingID] = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<PayrollBatchSummary | null>(null);
  const [lastResult, setLastResult] = useState<{ payPeriodID: number; result: ProcessPayrollResponse } | null>(null);

  const currentYear = year ? parseInt(year) : new Date().getFullYear();

  useEffect(() => {
    loadBatches();
  }, [year]);

  const loadBatches = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getPayrollBatches(currentYear);
      // Sort descending by payPeriodID so most recent is at top
      setBatches(data.sort((a, b) => b.payPeriodID - a.payPeriodID));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load payroll batches.";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessClick = (batch: PayrollBatchSummary) => {
    setConfirmTarget(batch);
  };

  const handleConfirm = async () => {
    if (!confirmTarget) return;
    const batch = confirmTarget;
    setConfirmTarget(null);
    setProcessingID(batch.payPeriodID);
    setLastResult(null);
    try {
      const result = await processPayroll(batch.payPeriodID);
      setLastResult({ payPeriodID: batch.payPeriodID, result });
      // Update local state so the row reflects Completed status without a full reload
      setBatches((prev) =>
        prev.map((b) =>
          b.payPeriodID === batch.payPeriodID
            ? {
                ...b,
                payrollProcessingBatchID: result.payrollProcessingBatchID,
                status: 2,
                processedAt: new Date().toISOString(),
                isReprocess: b.payrollProcessingBatchID !== null,
                employeesProcessed: result.employeesProcessed,
                exportLinesWritten: result.exportLinesWritten,
                prdaRecordCount: result.prdaRecordCount,
              }
            : b
        )
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Payroll processing failed.";
      const friendly = mapErrorMessage(msg);
      createPopup("Payroll Processing Error", friendly);
    } finally {
      setProcessingID(null);
    }
  };

  const mapErrorMessage = (msg: string): string => {
    if (msg.includes("No PayrollApproved timesheets found"))
      return "No PayrollApproved timesheets found for this pay period. Ensure timesheets have been payroll-approved before processing.";
    if (msg.includes("adjustment PRDA records"))
      return "Reprocessing is blocked because PRDA adjustment records exist for this pay period. To make corrections, create new PRDA adjustment records instead.";
    if (msg.includes("Not authorized"))
      return "You do not have permission to process payroll.";
    return "Payroll processing failed. Please try again. If the problem persists, check the batch status.";
  };

  const renderStatus = (batch: PayrollBatchSummary) => {
    if (batch.status === null) {
      return <span style={{ color: "#888" }}>Not processed</span>;
    }
    if (batch.status === 2) {
      return (
        <span style={{ color: "#27ae60" }}>
          Processed{batch.processedAt ? ` — ${getDateString(new Date(batch.processedAt))}` : ""}
          {batch.isReprocess ? " (reprocessed)" : ""}
        </span>
      );
    }
    if (batch.status === 3) {
      return <span style={{ color: "#e74c3c" }}>Failed</span>;
    }
    if (batch.status === 1) {
      return <span style={{ color: "#f39c12" }}>Processing…</span>;
    }
    return null;
  };

  const renderButton = (batch: PayrollBatchSummary) => {
    const isProcessing = processingID === batch.payPeriodID;
    const hasApproved = batch.payrollApprovedCount > 0;
    const isProcessed = batch.status === 2;
    const isFailed = batch.status === 3;

    if (!hasApproved && !isProcessed && !isFailed) return null;

    const label = isProcessed || isFailed ? "Reprocess" : "Process Payroll";
    const isReprocess = isProcessed || isFailed;

    return (
      <button
        className={`btn${isReprocess ? " btn-warning" : ""}`}
        disabled={isProcessing}
        onClick={() => handleProcessClick(batch)}
        style={{ fontSize: "0.85rem", padding: "4px 10px" }}
      >
        {isProcessing ? "Processing…" : label}
      </button>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirmTarget) return null;
    const batch = confirmTarget;
    const isReprocess = batch.status === 2 || batch.status === 3;
    const weekLabel = `Week ${batch.weekNumber}`;
    const endLabel = getDateString(new Date(batch.endDate));

    return (
      <div className="modal-overlay" onClick={() => setConfirmTarget(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <h3>{isReprocess ? `Reprocess Payroll — ${weekLabel}` : `Process Payroll — ${weekLabel}`}</h3>
          {isReprocess ? (
            <p>
              Reprocess payroll for <strong>{weekLabel}</strong>?<br />
              This will overwrite all existing batch export records and PRDA Original records.<br />
              If PRDA adjustment records exist for this pay period, reprocessing will be blocked.
            </p>
          ) : (
            <p>
              Process payroll for <strong>{weekLabel}</strong> (Pay Period ending {endLabel})?<br />
              This will lock in batch export records and PRDA hour records for all{" "}
              <strong>{batch.payrollApprovedCount}</strong> PayrollApproved employees.<br />
              This action can be reprocessed later if needed.
            </p>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setConfirmTarget(null)}>
              Cancel
            </button>
            <button className={`btn${isReprocess ? " btn-danger" : ""}`} onClick={handleConfirm}>
              {isReprocess ? "Reprocess" : "Process Payroll"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderResultBanner = () => {
    if (!lastResult) return null;
    const { result, payPeriodID } = lastResult;
    const batch = batches.find((b) => b.payPeriodID === payPeriodID);
    const weekLabel = batch ? `Week ${batch.weekNumber}` : `Pay Period ${payPeriodID}`;
    return (
      <div
        style={{
          background: "#eafaf1",
          border: "1px solid #27ae60",
          borderRadius: 6,
          padding: "12px 16px",
          marginBottom: 16,
          color: "#1a6b3c",
        }}
      >
        <strong>Payroll Processed — {weekLabel}</strong>
        <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
          <li>Employees processed: {result.employeesProcessed}</li>
          <li>Export lines written: {result.exportLinesWritten}</li>
          <li>
            PRDA records written: {result.prdaRecordCount} ({result.prdaEmployeeCount} employees)
          </li>
        </ul>
      </div>
    );
  };

  return (
    <PageWrapper title="Process Payroll" backgroundColor={Colors.White}>
      {loadError && (
        <div style={{ color: "#e74c3c", marginBottom: 12 }}>{loadError}</div>
      )}

      {renderResultBanner()}

      {confirmTarget && renderConfirmDialog()}

      <table className="data-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th>Week</th>
            <th>Pay Period</th>
            <th>Check Date</th>
            <th>Approved Timesheets</th>
            <th>Batch Status</th>
            <th>Last Run</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {batches.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: "center", color: "#888" }}>
                No pay periods found for {currentYear}.
              </td>
            </tr>
          )}
          {batches.map((batch) => (
            <tr key={batch.payPeriodID}>
              <td>Week {batch.weekNumber}</td>
              <td>
                {getDateString(new Date(batch.startDate))} – {getDateString(new Date(batch.endDate))}
              </td>
              <td>{getDateString(new Date(batch.checkDate))}</td>
              <td style={{ textAlign: "center" }}>{batch.payrollApprovedCount}</td>
              <td>{renderStatus(batch)}</td>
              <td style={{ fontSize: "0.85rem", color: "#555" }}>
                {batch.employeesProcessed != null
                  ? `${batch.employeesProcessed} emp / ${batch.exportLinesWritten} lines / ${batch.prdaRecordCount} PRDA`
                  : "—"}
              </td>
              <td>{renderButton(batch)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PageWrapper>
  );
};

export default ProcessPayrollPage;
